import type React from "react";
import { useState } from "react";

import { faEnvelope, faLockKeyhole } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import { FormProvider, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import type { z } from "zod";

import { authenticateWithEmail } from "routes/login/queries";
import { loginSchema } from "routes/login/utils/validation/emailPasswordSchemas";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUICheckBox, MUITextInput } from "shared/atoms/inputs";
import { Notification } from "shared/atoms/Notification";
import { Spinner } from "shared/atoms/Spinner";
import { getStorageValue, setStorageValue } from "utils/storage";

import { getSignedOutParams } from "../../utils/getSignedOutParams";
import { ForgotPasswordLink } from "../ForgotPasswordLink";
import { Form } from "../styledComponents";

type FormValues = z.infer<typeof loginSchema>;

export const EmailForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectUrl, signedOutError } = getSignedOutParams(location);
  const savedEmail = getStorageValue("login-remember-email");

  const [rememberMe, setRememberMe] = useState(Boolean(savedEmail));
  const successUrl = redirectUrl || "/dashboard";
  const [authRequest, setAuthRequest] = useState({ loading: false, error: false, message: "" });
  const form = useForm<FormValues>({
    defaultValues: { email: savedEmail || "", password: "" },
    resolver: zodResolver(loginSchema),
  });

  const {
    formState: { errors },
    handleSubmit,
    watch,
  } = form;

  const onSubmit: SubmitHandler<FormValues> = async body => {
    setStorageValue("login-remember-email", rememberMe ? body.email : "");
    setAuthRequest({ loading: true, error: false, message: "" });

    try {
      await authenticateWithEmail(body);
      navigate(successUrl);
    } catch (err: unknown) {
      let message = t("errors.generic_login");

      if (axios.isAxiosError(err)) {
        const { response } = err;

        if (response?.status === 400 || response?.status === 401 || response?.status === 422) {
          message = t("errors.invalid_login");
        }
      }

      setAuthRequest({ loading: false, error: true, message });
    }
  };

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <LoginLabel variant="subtitle1">{t("form.email")}</LoginLabel>
        <StyledMUITextInput
          name="email"
          type="email"
          placeholder={t("form.email")}
          // FIXME: type translation
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          error={errors.email?.message && t(errors?.email?.message)}
          startAdornment={<FontAwesomeIcon icon={faEnvelope} />}
          autoFocus={!savedEmail}
        />

        <Box mt={0.5} mb={3} width="100%">
          <MUICheckBox
            label={t("email.remember_me")}
            name="rememberEmail"
            onChange={() => setRememberMe(!rememberMe)}
            checked={rememberMe}
            unregistered
          />
        </Box>

        <LoginLabel variant="subtitle1">{t("form.password")}</LoginLabel>
        <StyledMUITextInput
          name="password"
          type="password"
          placeholder={t("form.password")}
          // FIXME: type translation
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          error={errors?.password?.message && t(errors.password.message)}
          startAdornment={<FontAwesomeIcon icon={faLockKeyhole} />}
          autoFocus={Boolean(savedEmail)}
        />

        {authRequest.error && (
          <Notification variant="danger" style={{ margin: "10px 0" }}>
            {authRequest?.message}
          </Notification>
        )}

        {signedOutError && (
          <Notification variant="danger" style={{ margin: "10px 0" }}>
            {signedOutError === "session_expired" ? t("errors.session_expired") : signedOutError}
          </Notification>
        )}

        <BaseButton fullWidth disabled={authRequest.loading} type="submit" style={{ marginTop: 24 }}>
          {authRequest.loading ? <Spinner small /> : t("buttons.sign_in")}
        </BaseButton>

        <Box mt={3}>
          <ForgotPasswordLink email={watch("email")} />
        </Box>
      </Form>
    </FormProvider>
  );
};

const StyledMUITextInput = styled(MUITextInput)`
  flex: initial;
  width: 100%;
`;

const LoginLabel = styled(Typography)`
  margin-bottom: 4px !important;
  width: 100%;
  color: ${({ theme }) => theme.colors.redesign.db80} !important;
`;
